import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { adminApiInZone, StaffApiError } from '../../lib/api-client';
import { type PortalZone } from '../../lib/zones';
import { usePassthroughZone } from '../../components/ZoneContext';
import { ZoneAmbiguousNotFound } from '../../components/ZoneIndicators';
import { useStaffMe, isMutating } from '../../hooks/useStaffMe';
import { listFixCards, setFixCardBeta, type StaffFixCard } from '../../lib/fix-guide-cards-api';
import { ActionIcon } from '../../components/ActionIcon';

/**
 * /admin/customers/:accountId/domains/:domainToken — domain detail (ADR-011;
 * relocated from /staff/customers by ADR-090 console consolidation).
 *
 * Status transitions allowed via PUT (per ADR-011's state machine):
 *   active <-> suspended only. archived is set via DELETE and is terminal.
 *   pending → active goes through the validate endpoint (3b onboarding flow),
 *   never the operational PUT — that transition is greyed out here.
 *
 * allowedOrigins edits go through the same URL validator the server uses
 * (HTTPS only, no localhost, no private IP literals, etc.) — server is
 * authoritative; this UI just rejects obviously-bad input early.
 *
 * ADR-122 Pattern 2 — ZONE PASSTHROUGH. A domain belongs to an account, which lives in exactly
 * one zone, so this page reads `?zone=` off its URL and sends ONE call there — never a fan-out,
 * which would return one hit and N−1 404s. The link that reaches here already carries the zone
 * (CustomersPage tags its rows, and AdminCustomerDetailPage passes it on); a link that does
 * NOT — an old bookmark, a URL pasted into chat — falls back to the home zone, and if that
 * lookup misses, the page says which zone it asked rather than reporting a bare "not found"
 * that is accurate about the request and wrong about the world.
 */

interface DomainDetail {
  domainToken: string;
  accountId: string;
  hostname: string;
  url: string;
  status: string;
  allowedOrigins: string[];
}

export function DomainDetailPage() {
  const { domainToken } = useParams<{ accountId: string; domainToken: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const zoneParam = searchParams.get('zone') ?? undefined;
  // ADR-123 — URL zone, else preferred, else home; reported to the navigation bar.
  const zone: PortalZone = usePassthroughZone(zoneParam);
  const meState = useStaffMe();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DomainDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [origins, setOrigins] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  // P48 — staff Fix Card beta tagging.
  const [fixCards, setFixCards] = useState<StaffFixCard[] | null>(null);
  const [fixCardsError, setFixCardsError] = useState<string | null>(null);
  const [betaBusyRef, setBetaBusyRef] = useState<string | null>(null);
  // A 404 on a link that carried NO zone is the ambiguous case — the domain may live in
  // another jurisdiction. A 404 on a link that DID name a zone is a genuine miss.
  const [zoneAmbiguous, setZoneAmbiguous] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (!domainToken) return;
    setError(null);
    setZoneAmbiguous(false);
    try {
      const data = await adminApiInZone<DomainDetail>(zone, `/admin/domains/${encodeURIComponent(domainToken)}`);
      setDetail(data);
      setOrigins(data.allowedOrigins);
    } catch (err) {
      setZoneAmbiguous(!zoneParam && err instanceof StaffApiError && err.status === 404);
      setError(err instanceof Error ? err.message : 'Failed to load domain.');
    }
  }, [domainToken, zone, zoneParam]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // P48 — load the domain's Fix Cards so staff can tag beta/test data.
  useEffect(() => {
    if (!detail) return;
    let cancelled = false;
    setFixCardsError(null);
    void listFixCards(detail.accountId, detail.domainToken, zone)
      .then((cards) => { if (!cancelled) setFixCards(cards); })
      .catch((err) => { if (!cancelled) setFixCardsError(err instanceof Error ? err.message : 'Failed to load Fix Cards.'); });
    return () => { cancelled = true; };
  }, [detail, zone]);

  async function toggleBeta(card: StaffFixCard): Promise<void> {
    if (!detail) return;
    setBetaBusyRef(card.ref);
    try {
      const { beta } = await setFixCardBeta(detail.accountId, detail.domainToken, card.ref, !card.beta, zone);
      setFixCards((prev) => prev?.map((c) => (c.ref === card.ref ? { ...c, beta } : c)) ?? prev);
    } catch (err) {
      setFixCardsError(err instanceof Error ? err.message : 'Failed to update the beta marker.');
    } finally {
      setBetaBusyRef(null);
    }
  }

  async function saveOrigins(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!detail) return;
    const cleaned = origins.map((o) => o.trim()).filter((o) => o.length > 0);
    setBusy(true);
    setError(null);
    try {
      await adminApiInZone(zone, `/admin/domains/${encodeURIComponent(detail.domainToken)}`, {
        method: 'PUT',
        body: JSON.stringify({ allowedOrigins: cleaned }),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function flipStatus(target: 'active' | 'suspended'): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await adminApiInZone(zone, `/admin/domains/${encodeURIComponent(detail.domainToken)}`, {
        method: 'PUT',
        body: JSON.stringify({ status: target }),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status change failed.');
    } finally {
      setBusy(false);
    }
  }

  async function archive(): Promise<void> {
    if (!detail) return;
    setBusy(true);
    setError(null);
    try {
      await adminApiInZone(zone, `/admin/domains/${encodeURIComponent(detail.domainToken)}`, {
        method: 'DELETE',
      });
      // Carry the zone back to the customer page, or the operator lands on a 404 for the
      // account they just archived a domain under.
      navigate(`/admin/customers/${encodeURIComponent(detail.accountId)}?zone=${zone.code}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Archive failed.');
      setBusy(false);
    }
  }

  if (meState.status === 'loading' || detail === null) {
    return (
      <main className="portal-page">
        <h1>Domain</h1>
        {zoneAmbiguous ? (
          <ZoneAmbiguousNotFound
            askedZone={zone.code}
            noun="This domain"
            // Re-running through the URL keeps the resolved zone in the address bar, so the
            // link the operator then shares carries it.
            onTryZone={(code) => setSearchParams({ zone: code })}
          />
        ) : error
          ? <div role="alert" className="portal-error">{error}</div>
          : <p>Loading…</p>}
      </main>
    );
  }
  if (meState.status === 'error') {
    return (
      <main className="portal-page">
        <h1>Domain</h1>
        <div role="alert" className="portal-error">{meState.message}</div>
      </main>
    );
  }

  const canMutate = isMutating(meState.me.role);

  return (
    <main className="portal-page">
      <h1>{detail.hostname}</h1>
      <p className="portal-help">
        <Link to={`/admin/customers/${encodeURIComponent(detail.accountId)}`}>← Back to customer</Link>
      </p>
      {error && <div role="alert" className="portal-error">{error}</div>}

      <section className="portal-card">
        <header><h2>Status</h2></header>
        <dl className="portal-meta">
          <dt>Current</dt>
          <dd><span className={`portal-badge portal-badge-${detail.status}`}>{detail.status}</span></dd>
          <dt>Token</dt><dd><code>{detail.domainToken}</code></dd>
          <dt>URL</dt><dd>{detail.url}</dd>
        </dl>
        {canMutate && (
          <div className="portal-modal-actions">
            {detail.status === 'active' && (
              <button type="button" className="portal-btn" disabled={busy} onClick={() => flipStatus('suspended')}>Suspend</button>
            )}
            {detail.status === 'suspended' && (
              <button type="button" className="portal-btn" disabled={busy} onClick={() => flipStatus('active')}>Reactivate</button>
            )}
            {detail.status !== 'archived' && (
              <button
                type="button"
                disabled={busy}
                className="portal-button-danger"
                onClick={() => setConfirmArchive(true)}
              >
                Archive (terminal)
              </button>
            )}
            {detail.status === 'archived' && (
              <p className="portal-help">Archived is terminal — no further edits possible.</p>
            )}
          </div>
        )}
        {!canMutate && (
          <p className="portal-help">Read-only view. Status changes require Mutating-tier access.</p>
        )}
      </section>

      <section className="portal-card">
        <header><h2>Allowed origins</h2></header>
        {!canMutate || detail.status === 'archived' ? (
          <ul>
            {detail.allowedOrigins.map((o, i) => <li key={i}><code>{o}</code></li>)}
          </ul>
        ) : (
          <form onSubmit={saveOrigins} aria-busy={busy} className="portal-wizard-form">
            <p className="portal-help">HTTPS only. No localhost or private IPs. Max 10 origins.</p>
            {origins.map((origin, idx) => (
              <div key={idx} className="portal-origin-row">
                <input
                  type="url"
                  value={origin}
                  onChange={(e) => {
                    const next = [...origins];
                    next[idx] = e.target.value;
                    setOrigins(next);
                  }}
                />
                <button
                  type="button"
                  className="portal-link-button-secondary"
                  onClick={() => setOrigins(origins.filter((_, i) => i !== idx))}
                  disabled={origins.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="portal-wizard-actions">
              <button
                type="button"
                className="portal-btn"
                disabled={origins.length >= 10}
                onClick={() => setOrigins([...origins, ''])}
              >
                + Add origin
              </button>
              <button type="submit" className="portal-btn" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        )}
      </section>

      {/* P48 — Fix Card beta/test tagging (staff). Mutating tier toggles; others
          see read-only. The matchKey metric is the recognisable card label. */}
      <section className="portal-card">
        <header><h2>Fix Cards — beta tagging</h2></header>
        <p className="portal-help">
          Tag a Fix Card as <strong>beta/test data</strong> so UAT can tell it apart from real
          recommendations. A tagged card shows a muted “BETA” badge in the subscriber portal.
        </p>
        {fixCardsError && <div role="alert" className="portal-error">{fixCardsError}</div>}
        {fixCards === null && !fixCardsError ? (
          <p className="portal-muted">Loading Fix Cards…</p>
        ) : fixCards && fixCards.length === 0 ? (
          <p className="portal-muted">No Fix Cards for this domain yet.</p>
        ) : fixCards ? (
          <table className="portal-users-table">
            <thead>
              <tr>
                <th scope="col">Ref</th>
                <th scope="col">Card</th>
                <th scope="col">Status</th>
                <th scope="col">Beta</th>
                {canMutate && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {fixCards.map((c) => (
                <tr key={c.ref}>
                  <td><code>#{c.ref}</code></td>
                  <td>{c.label}</td>
                  <td><span className={`portal-badge portal-badge-${c.status}`}>{c.status}</span></td>
                  <td>{c.beta ? <span className="portal-badge">BETA</span> : <span className="portal-muted">—</span>}</td>
                  {canMutate && (
                    <td>
                      <ActionIcon act={c.beta ? 'pause' : 'review'} icon="flag"
                        label={c.beta ? 'Clear beta' : 'Mark beta'}
                        tip={betaBusyRef === c.ref ? 'Saving…' : c.beta ? 'Clear beta — the card goes to every customer' : 'Mark beta — only beta customers see the card'}
                        disabled={betaBusyRef === c.ref}
                        onClick={() => void toggleBeta(c)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {!canMutate && fixCards && fixCards.length > 0 && (
          <p className="portal-help">Read-only view. Tagging requires Mutating-tier access.</p>
        )}
      </section>

      {confirmArchive && (
        <div
          className="portal-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm archive"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setConfirmArchive(false);
          }}
        >
          <div className="portal-modal">
            <button
              type="button"
              className="portal-modal-close"
              onClick={() => setConfirmArchive(false)}
              disabled={busy}
              aria-label="Close"
            >
              ×
            </button>
            <h2>Archive {detail.hostname}?</h2>
            <p className="portal-error">
              Archive is terminal. The KV entry is removed, edge beacons return 403.
              To restore, the customer would re-register from scratch with a new token.
            </p>
            <div className="portal-modal-actions">
              <button type="button" className="portal-button-danger" disabled={busy} onClick={archive}>
                {busy ? 'Archiving…' : 'Confirm archive'}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmArchive(false)} className="portal-link-button-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
